// Aktivitätsvergleich (Agent 7A, R-10): Tabelle Nutzer mal Kennzahl mit Summenzeile, zweizeiligem Kopf, Sortierung per Klick und Spaltenwahl.
// Standard „Dilsar gegen Simon“ (Vorgabe-Bericht).
import { useCallback, useMemo, useState } from "react";
import { berichteApi, type Kennzahl } from "../api/berichte";
import { Avatar, Laden } from "../components/Bausteine";
import { Icon } from "../components/Icon";
import { BerichtSeite } from "./BerichtSeite";
import { katalogNachId, useGeladen, useKatalog } from "./daten";
import { wertText } from "./format";
import { KennzahlWahl } from "./KennzahlWahl";
import { kennzahlenAus, type Konfig } from "./konfig";
import { useBerichtSteuerung } from "./steuerung";
import { useBerichtFilter } from "./zustand";

function SpalteNeu({ katalog, verwendet, onWahl }: { katalog: Kennzahl[]; verwendet: Set<string>; onWahl: (id: string) => void }) {
  const [offen, setOffen] = useState(false);
  const [anker, setAnker] = useState<HTMLButtonElement | null>(null);
  return (
    <>
      <button ref={setAnker} type="button" className="symbolknopf symbolknopf--rand" title="Spalte hinzufügen" aria-label="Spalte hinzufügen" aria-haspopup="dialog" onClick={() => setOffen((o) => !o)}>
        <Icon name="plus" groesse={14} />
      </button>
      <KennzahlWahl katalog={katalog} anker={anker} offen={offen} onSchliessen={() => setOffen(false)} ausgeschlossen={verwendet} onWahl={onWahl} />
    </>
  );
}

export function Vergleich() {
  const filter = useBerichtFilter("diese_woche");
  const { katalog, fehler: katalogFehler } = useKatalog();
  const [spalten, setSpalten] = useState<string[]>([]);
  const [sortierung, setSortierung] = useState<{ id: string; abwaerts: boolean } | null>(null);
  const zusatz = useMemo<Konfig>(() => ({ kennzahlen: spalten, gruppieren: "nutzer" }), [spalten]);
  const steuerung = useBerichtSteuerung({ art: "vergleich", filter, zusatz, onAnwenden: (k) => setSpalten(kennzahlenAus(k)) });
  const nachId = useMemo(() => katalogNachId(katalog), [katalog]);
  const sichtbar = spalten.filter((id) => nachId.has(id));
  const { eingabe, eingabeSchluessel } = filter;
  const abfrage = useGeladen(
    () => berichteApi.abfrage({ ...eingabe, vergleich: undefined, kennzahlen: sichtbar, gruppieren: "nutzer" }),
    `${eingabeSchluessel}|${sichtbar.join(",")}`,
    !!katalog && sichtbar.length > 0 && steuerung.geladen,
  );
  const zeilen = useMemo(() => {
    const g = [...(abfrage.daten?.gruppen ?? [])];
    if (sortierung) g.sort((a, b) => ((b.werte[sortierung.id] ?? -1) - (a.werte[sortierung.id] ?? -1)) * (sortierung.abwaerts ? 1 : -1) || a.titel.localeCompare(b.titel, "de"));
    return g;
  }, [abfrage.daten, sortierung]);
  const verwendet = useMemo(() => new Set(spalten), [spalten]);
  const entfernen = useCallback((id: string) => setSpalten((a) => a.filter((x) => x !== id)), []);
  const sortiere = (id: string) => setSortierung((s) => (s?.id === id ? (s.abwaerts ? { id, abwaerts: false } : null) : { id, abwaerts: true }));
  const param = { ...eingabe, vergleich: undefined, kennzahlen: sichtbar, gruppieren: "nutzer" };

  return (
    <BerichtSeite
      art="vergleich"
      filter={filter}
      steuerung={steuerung}
      ohneVergleich
      fehler={abfrage.fehler ?? katalogFehler}
      onNochmal={abfrage.neuLaden}
      csv={sichtbar.length ? [{ titel: "Tabelle als CSV laden", art: "abfrage", parameter: param }] : []}
    >
      {!katalog ? (
        <Laden />
      ) : sichtbar.length === 0 ? (
        <p className="leise">Noch keine Spalten gewählt.</p>
      ) : (
        <div className="tabelle-rahmen bericht-tabelle" aria-busy={abfrage.laedt}>
          <table className="tabelle">
            <thead>
              <tr>
                <th className="bericht-tabelle__nutzer">Nutzer</th>
                {sichtbar.map((id) => {
                  const k = nachId.get(id)!;
                  const an = sortierung?.id === id;
                  return (
                    <th key={id} className="bericht-tabelle__zahl" aria-sort={an ? (sortierung!.abwaerts ? "descending" : "ascending") : "none"}>
                      <div className="bericht-spaltenkopf">
                        <button type="button" className="bericht-spaltenkopf__knopf" onClick={() => sortiere(id)} title="Nach dieser Spalte sortieren">
                          <span className="bericht-spaltenkopf__titel">{k.titel}</span>
                          <span className="bericht-spaltenkopf__unter">{k.untertitel?.toLocaleLowerCase("de")}</span>
                        </button>
                        {an ? <Icon name={sortierung!.abwaerts ? "pfeil-runter" : "pfeil-hoch"} groesse={12} /> : null}
                        <button type="button" className="bericht-spaltenkopf__x" aria-label={`Spalte „${k.titel}“ entfernen`} title="Spalte entfernen" onClick={() => entfernen(id)}>
                          <Icon name="schliessen" groesse={11} />
                        </button>
                      </div>
                    </th>
                  );
                })}
                <th className="bericht-tabelle__neu">
                  <SpalteNeu katalog={katalog} verwendet={verwendet} onWahl={(id) => setSpalten((a) => (a.includes(id) ? a : [...a, id]))} />
                </th>
              </tr>
            </thead>
            <tbody>
              {zeilen.length === 0 && !abfrage.laedt ? (
                <tr>
                  <td colSpan={sichtbar.length + 2} className="leise">Keine Daten im Zeitraum.</td>
                </tr>
              ) : null}
              {zeilen.map((g, i) => (
                <tr key={g.schluessel}>
                  <td className="bericht-tabelle__nutzer">
                    <span className="bericht-nutzer">
                      <span className="leise bericht-nutzer__platz">{i + 1}</span>
                      <Avatar name={g.titel} id={g.schluessel} groesse={20} />
                      {g.titel}
                    </span>
                  </td>
                  {sichtbar.map((id) => (
                    <td key={id} className="bericht-tabelle__zahl">{wertText(g.werte[id], nachId.get(id)!.einheit)}</td>
                  ))}
                  <td />
                </tr>
              ))}
            </tbody>
            {zeilen.length > 0 && abfrage.daten?.summe ? (
              <tfoot>
                <tr>
                  <td className="bericht-tabelle__nutzer"><strong>Gesamt</strong></td>
                  {sichtbar.map((id) => (
                    <td key={id} className="bericht-tabelle__zahl"><strong>{wertText(abfrage.daten!.summe![id], nachId.get(id)!.einheit)}</strong></td>
                  ))}
                  <td />
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      )}
    </BerichtSeite>
  );
}
